import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  loadLog, saveLog, withVerseRecorded, emptyLog, isValidLog, clearLog, dateKey,
  migrateLog, cycleProgress, khatmCount
} from '../utils/readingLog';
import {
  GOAL_STORAGE_KEY, isValidGoal, goalProgress, goalStreak, goalProjection,
  readingStreak, hasReadToday, migrateGoal
} from '../utils/goals';
import { syncPush } from '../utils/push';

export const GoalsContext = createContext();

const loadGoal = () => {
  try {
    const raw = localStorage.getItem(GOAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const migrated = migrateGoal(parsed);
    return isValidGoal(migrated) ? migrated : null;
  } catch (error) {
    console.error('Could not read the saved goal:', error);
    return null;
  }
};

export const GoalsProvider = ({ children }) => {
  const [goal, setGoalState] = useState(loadGoal);
  const [log, setLog] = useState(loadLog);
  // Re-evaluated on the hour so a period rolling over is noticed without a reload
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const setGoal = useCallback((nextGoal) => {
    if (nextGoal === null) {
      localStorage.removeItem(GOAL_STORAGE_KEY);
      setGoalState(null);
      return;
    }
    if (!isValidGoal(nextGoal)) return;
    localStorage.setItem(GOAL_STORAGE_KEY, JSON.stringify(nextGoal));
    setGoalState(nextGoal);
  }, []);

  // Called once a verse has been on screen long enough to count as read.
  const recordVerseRead = useCallback((surah, verse) => {
    setLog((current) => {
      const updated = withVerseRecorded(current, surah, verse);
      // withVerseRecorded returns the same object when the verse is already
      // logged, so an unchanged reference means nothing to write.
      if (updated !== current) saveLog(updated);
      return updated;
    });
  }, []);

  const resetLog = useCallback(() => setLog(clearLog()), []);

  const restoreBackup = useCallback((payload) => {
    if (!payload || typeof payload !== 'object') return { ok: false, reason: 'That file is not a backup.' };
    if (!isValidLog(payload.log)) return { ok: false, reason: 'That backup has no reading history in it.' };

    const restored = migrateLog(payload.log);
    saveLog(restored);
    setLog(restored);
    if (isValidGoal(payload.goal)) setGoal(payload.goal);
    return { ok: true };
  }, [setGoal]);

  const today = useMemo(() => dateKey(now), [now]);

  const progress = useMemo(
    () => (goal ? goalProgress(goal, log, now) : null),
    [goal, log, now]
  );
  const streak = useMemo(
    () => (goal ? goalStreak(goal, log, now) : 0),
    [goal, log, now]
  );
  const projection = useMemo(
    () => (goal ? goalProjection(goal, log, now) : null),
    [goal, log, now]
  );
  const daysInARow = useMemo(() => readingStreak(log, now), [log, now]);
  const khatms = useMemo(() => khatmCount(log), [log]);
  const cycle = useMemo(() => cycleProgress(log), [log]);
  const readToday = useMemo(() => hasReadToday(log, now), [log, now]);

  // Once the goal is met, tell the server so it stops reminding for today.
  // Silent and best-effort: no subscription, or no backend, changes nothing.
  const metOn = progress?.met ? today : null;
  useEffect(() => {
    if (!metOn || !goal) return;
    syncPush({ hours: goal.reminderHours || [], enabled: goal.reminderEnabled !== false, goalMetOn: metOn });
  }, [metOn, goal]);

  const value = useMemo(() => ({
    goal, setGoal,
    log, recordVerseRead, resetLog, restoreBackup,
    progress, streak, projection,
    daysInARow, readToday, khatms, cycle,
    today,
    now
  }), [goal, setGoal, log, recordVerseRead, resetLog, restoreBackup, progress, streak, projection, daysInARow, readToday, khatms, cycle, today, now]);

  return <GoalsContext.Provider value={value}>{children}</GoalsContext.Provider>;
};

export const useGoals = () => {
  const context = useContext(GoalsContext);
  if (!context) throw new Error('useGoals must be used within a GoalsProvider');
  return context;
};

export { emptyLog };
